import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui-kit/dialog';
import { Button } from '@/components/ui-kit/button';
import { Textarea } from '@/components/ui-kit/textarea';
import { Brain } from 'lucide-react';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuerySSE } from '@/modules/gpt-chats/hooks/use-agents';
import { ISSEQueryAgentPayload } from '@/modules/gpt-chats/types/agent.service.type';
import { parseSSEBuffer } from '@/modules/gpt-chats/shared/utils/parse-sse';
import { BASE_PROMPT_GENERATION_PROMPT } from '@/modules/gpt-chats/constants';
import { AnimatedDots } from '@/modules/gpt-chats/shared/utils/chat-event-messages';
import { useTranslation } from 'react-i18next';

type BasePromptAIModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onUse: (basePrompt: string) => void;
  agentName?: string;
  basePromptValue: string;
  tool_ids?: string[];
};

export const BasePromptAIModal = ({
  isOpen,
  onClose,
  onUse,
  agentName,
  basePromptValue,
  tool_ids,
}: BasePromptAIModalProps) => {
  const { t } = useTranslation();
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [generatedBasePrompt, setGeneratedBasePrompt] = useState('');
  const [statusMessage, setStatusMessage] = useState('GENERATING');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { streamQuery } = useQuerySSE();

  const handleClose = useCallback(() => {
    onClose();
    setGeneratedBasePrompt('');
    setIsGenerating(false);
  }, [onClose]);

  const generatePrompt = useCallback(async () => {
    try {
      setIsGenerating(true);
      setGeneratedBasePrompt('');
      setStatusMessage('GENERATING');

      const payload: ISSEQueryAgentPayload = {
        query: basePromptValue ?? agentName,
        base_prompt: BASE_PROMPT_GENERATION_PROMPT,
        last_n_turns: 5,
        enable_summary: true,
        enable_tools: true,
        enable_next_suggestion: false,
        call_from: 'blocks',
        tool_ids,
      };

      const stream = await streamQuery(payload);
      const reader = stream.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let isDone = false;

      while (!isDone) {
        const { done, value } = await reader.read();
        isDone = done;
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const { events, remaining } = parseSSEBuffer(buffer);
        buffer = remaining;

        events.forEach(({ eventType, eventData }) => {
          const content: string = (eventData.result || eventData.message) as string;
          switch (eventType) {
            case 'chat_response':
            case 'final_answer':
              if (content) {
                setGeneratedBasePrompt(content);
                setIsGenerating(false);
              }
              break;
            case 'error':
              setGeneratedBasePrompt(t('ERROR_GENERATING_PROMPT'));
              setIsGenerating(false);
              break;
          }
        });
      }
    } catch (error) {
      setGeneratedBasePrompt(t('FAILED_TO_GENERATE_PROMPT'));
      setIsGenerating(false);
    }
  }, [streamQuery, tool_ids, agentName, basePromptValue, t]);

  useEffect(() => {
    if (isOpen && generatedBasePrompt === '' && !isGenerating) {
      generatePrompt();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleUse = () => {
    onUse(generatedBasePrompt);
    handleClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">{t('BASE_PROMPT')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div>
            {isGenerating ? (
              <div className="flex min-h-[120px] items-center justify-center rounded-lg border bg-card">
                <div className="flex flex-col items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary">
                    <Brain className="h-6 w-6 animate-pulse text-white" />
                  </div>
                  <div className="flex flex-row items-center gap-2">
                    <span className="text-normal text-medium-emphasis">{t(statusMessage)}</span>
                    <div className="mt-1 flex flex-col gap-3">
                      <AnimatedDots />
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="relative">
                <Textarea
                  ref={textareaRef}
                  value={generatedBasePrompt}
                  onChange={(e) => setGeneratedBasePrompt(e.target.value)}
                  placeholder={t('BASE_PROMPT_GENERATION_PLACEHOLDER')}
                  rows={12}
                  className="resize-none border bg-card"
                />
                <div className="mt-2 text-right text-sm text-medium-emphasis">
                  {t('CHARACTER_COUNT', { count: generatedBasePrompt.length })}
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={generatePrompt}
            disabled={isGenerating}
            className="mr-auto pl-0 text-primary hover:bg-transparent hover:text-high-emphasis"
          >
            {t('REGENERATE')}
          </Button>
          <Button variant="outline" onClick={handleClose} className="px-8">
            {t('CANCEL')}
          </Button>
          <Button
            onClick={handleUse}
            disabled={isGenerating || !generatedBasePrompt}
            className="bg-primary px-8 text-white hover:bg-primary/90"
          >
            {t('USE')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
